import React, { useState, useEffect } from "react";
import { Table, Row, Col, Collapse, Button, Spinner } from "reactstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import { toast } from "react-toastify";
// componentes
import Pagination from "../../../../components/Paginationv3";
import SectionComponent from "../../../Apps/Personalization/SectionComponent";
import LoadingSkelentonTable from "../../../../components/loadingSkeletonTable";
import WaitExport from "../../../../components/WaitExport";
import ButtonSpinner from "../../../../components/Buttons/ButtonSpinner";
import ModalWordCloud from "./wordCloud";
import ChatFixed from "./chatFixed";
// service
import {
  getChat,
  disableChat,
} from "../../../../utils/services/dashboardSuperScreen";
// utils
import useDebounce from "../../../../utils/hooks/useDebounce";
import { useFirestore } from "../../../../utils/firestoreProvider";
import { createXlsx, formatDate } from "./exports/createXlsxChat";

export default function TableChat(props) {
  // firestore
  const firestoreData = useFirestore();
  // ids do evento
  const eventId = props.idEvent;
  const idChat = props.idChat;

  // state de pesquisa
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  // state de paginação
  const [currentIndex, setCurrentIndex] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // força um novo fetch mesmo quando nenhum filtro mudou (botão Refresh)
  const [refreshKey, setRefreshKey] = useState(0);
  // state do collapse de opções (mobile)
  const [isOpen, setIsOpen] = useState(false);
  // state do modal da nuvem de palavras
  const [modalWordCloud, setModalWordCloud] = useState(false);
  // state de deletar mensagem
  const [deleteData, setDeleteData] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [successDelete, setSuccessDelete] = useState(false);
  // state de fixar mensagem
  const [confirmChatFix, setConfirmChatFix] = useState(false);
  const [successChatFix, setSuccessChatFix] = useState(false);
  const [chatFixData, setChatFixData] = useState({});
  // state de desafixar mensagem
  const [confirmRemoveChatFix, setConfirmRemoveChatFix] = useState(false);
  const [successRemoveChatFix, setSuccessRemoveChatFix] = useState(false);
  // state do alerta de erro das ações (deletar, fixar e desafixar)
  const [errorMessage, setErrorMessage] = useState("");
  // state de exportação
  const [exportInProgress, setExportInProgress] = useState(false);
  const [finishExport, setFinishExport] = useState(false);
  // state de carregamento dos botões
  const [loading, setLoading] = useState(false);
  const [loadingChatFixed, setLoadingChatFixed] = useState(false);

  const chat = props.tableData.chat;
  const countData = chat.total ?? 0;

  // atualiza os dados do chat no state compartilhado do dashboard
  function updateChat(values) {
    props.setTableData((state) => ({
      ...state,
      chat: { ...state.chat, ...values },
    }));
  }

  // fixa (enabled = true) ou desafixa (enabled = false) a mensagem selecionada
  async function verifyFixChat(enabled) {
    setLoading(true);
    try {
      await firestoreData.fixeChat({
        msg: chatFixData.msg,
        enabled,
        avatar: chatFixData.avatar,
        name: chatFixData.name,
        eventIdChat: eventId,
        chatId: idChat,
        mobileapps: props.mobileapps,
      });

      if (enabled) {
        setConfirmChatFix(false);
        setSuccessChatFix(true);
        setLoadingChatFixed(true);
      } else {
        setConfirmRemoveChatFix(false);
        setSuccessRemoveChatFix(true);
        setLoadingChatFixed(false);
      }
    } catch (error) {
      setConfirmChatFix(false);
      setConfirmRemoveChatFix(false);
      setErrorMessage(
        enabled ? "Erro ao fixar a mensagem." : "Erro ao desafixar a mensagem."
      );
    } finally {
      setLoading(false);
    }
  }

  // inicia a exportação das mensagens em xlsx
  function exportReport() {
    createXlsx({
      eventId,
      transmissionID: idChat,
      search: "",
      countData,
      mobileapps: props.mobileapps,
      setFinishExport,
      setExportInProgress,
    });
    setExportInProgress(true);
  }

  // desativa a mensagem selecionada no chat
  async function funcDeleteChat() {
    setLoading(true);
    try {
      const res = await disableChat({
        eventId,
        transmissionID: idChat,
        chatId: deleteData.id,
        mobileapps: props.mobileapps,
      });

      setConfirmDelete(false);
      if (res?.success === true) {
        setSuccessDelete(true);
      } else {
        setErrorMessage("Erro ao deletar a mensagem.");
      }
    } catch (error) {
      setConfirmDelete(false);
      setErrorMessage("Erro ao deletar a mensagem.");
    } finally {
      setLoading(false);
    }
  }

  // atualiza o termo pesquisado e volta para a primeira página, senão a busca
  // sairia com o offset da página em que o usuário estava
  function handleSearch(value) {
    setSearch(value.toLowerCase());
    setCurrentIndex(1);
  }

  // volta a listagem para o estado inicial. O incremento do refreshKey dispara o
  // refetch pelo useEffect, já com o state atualizado, em vez de chamar o fetch
  // aqui com os valores antigos do render
  function resetPagination() {
    setSearch("");
    setItemsPerPage(10);
    setCurrentIndex(1);
    setRefreshKey((key) => key + 1);
  }

  useEffect(() => {
    // enquanto o debounce não fecha, search e debouncedSearch divergem e o fetch
    // sairia com o termo antigo. O próprio debounce dispara este efeito de novo
    if (search !== debouncedSearch) return;
    if (!props.mobileapps) return;

    // ignora respostas de requisições antigas (página ou pesquisa anterior)
    let ignore = false;

    updateChat({ loading: true });
    getChat({
      eventId,
      transmissionID: idChat,
      pageSize: itemsPerPage,
      offset: currentIndex - 1,
      search: debouncedSearch,
      mobileapps: props.mobileapps,
    })
      .then((res) => {
        if (ignore) return;

        if (res?.success) {
          updateChat({ loading: false, data: res.data, total: res.total });
        } else {
          updateChat({ loading: false });
          toast.error("Erro ao buscar mensagens do chat.");
        }
      })
      .catch(() => {
        if (ignore) return;

        updateChat({ loading: false });
        toast.error("Erro ao buscar mensagens do chat.");
      });

    return () => {
      ignore = true;
    };
  }, [
    debouncedSearch,
    itemsPerPage,
    currentIndex,
    props.mobileapps,
    refreshKey,
  ]);

  return (
    <div>
      <WaitExport
        isOpen={exportInProgress}
        name="Relatório"
        preposicao="dos"
        finishExport={finishExport}
        setFinishExport={setFinishExport}
        setExportInProgress={setExportInProgress}
      />

      {modalWordCloud && (
        <ModalWordCloud
          isOpen={modalWordCloud}
          toggle={() => setModalWordCloud(!modalWordCloud)}
          eventId={eventId}
          idChat={idChat}
          type={"chat"}
          mobileapps={props.mobileapps}
        />
      )}

      {confirmDelete ? (
        <SweetAlert
          title={`Deseja deletar o chat ${deleteData.msg}?`}
          warning
          showConfirm={false}
          showCancel={false}
        >
          <div className="d-flex flex-column align-items-center">
            <p className="mb-2">Você não poderá reverter essa ação!</p>
            <div className="d-flex">
              <Button
                color={loading ? "secondary" : "success"}
                className="waves-effect waves-light"
                style={{ width: loading ? "auto" : 100, marginRight: 10 }}
                disabled={loading}
                onClick={() => funcDeleteChat()}
              >
                {loading && <Spinner size="sm" className="me-1" />}
                Confirmar
              </Button>
              <Button
                color="danger"
                className="waves-effect waves-light"
                style={{ width: 100 }}
                disabled={loading}
                onClick={() => setConfirmDelete(false)}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </SweetAlert>
      ) : null}

      {successDelete ? (
        <SweetAlert
          success
          title="Deletado"
          onConfirm={() => {
            setSuccessDelete(false);
            setRefreshKey((key) => key + 1);
          }}
        >
          Chat deletado com sucesso
        </SweetAlert>
      ) : null}

      {errorMessage ? (
        <SweetAlert error title="Erro" onConfirm={() => setErrorMessage("")}>
          {errorMessage}
        </SweetAlert>
      ) : null}

      {confirmChatFix ? (
        <SweetAlert
          title={`Deseja fixar o chat ${chatFixData.msg}?`}
          warning
          showConfirm={false}
          showCancel={false}
        >
          <div className="d-flex justify-content-center">
            <ButtonSpinner
              isLoading={loading}
              title="Confirmar"
              action="U"
              size={100}
              onClick={() => verifyFixChat(true)}
            />
            <Button
              color="danger"
              className="waves-effect waves-light"
              style={{ width: 100, marginLeft: 10 }}
              disabled={loading}
              onClick={() => setConfirmChatFix(false)}
            >
              Cancelar
            </Button>
          </div>
        </SweetAlert>
      ) : null}

      {successChatFix ? (
        <SweetAlert
          success
          title="Fixado"
          onConfirm={() => {
            setSuccessChatFix(false);
          }}
        >
          Chat fixado com sucesso
        </SweetAlert>
      ) : null}

      {confirmRemoveChatFix ? (
        <SweetAlert
          title={`Deseja desafixar o chat ${chatFixData.msg}?`}
          warning
          showConfirm={false}
          showCancel={false}
        >
          <div className="d-flex justify-content-center">
            <ButtonSpinner
              isLoading={loading}
              title="Confirmar"
              action="U"
              size={100}
              onClick={() => verifyFixChat(false)}
            />
            <Button
              color="danger"
              className="waves-effect waves-light"
              style={{ width: 100, marginLeft: 10 }}
              disabled={loading}
              onClick={() => setConfirmRemoveChatFix(false)}
            >
              Cancelar
            </Button>
          </div>
        </SweetAlert>
      ) : null}

      {successRemoveChatFix ? (
        <SweetAlert
          success
          title="Desafixado"
          onConfirm={() => {
            setSuccessRemoveChatFix(false);
          }}
        >
          Chat desafixado com sucesso
        </SweetAlert>
      ) : null}

      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          margin: "10px 0",
        }}
      >
        <form
          className="app-search d-none d-lg-block events-input-search"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="position-relative">
            <input
              type="text"
              className="form-control"
              placeholder="Pesquisar..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
            <span className="bx bx-search-alt" />
          </div>
        </form>

        <div className="mobile-btn-section" style={{ width: "100%" }}>
          <Row className="mobile-btns-top">
            <form
              className="app-search"
              style={{ padding: "0px" }}
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Pesquisar..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <span className="bx bx-search-alt" />
              </div>
            </form>
          </Row>
          {chat.data.length > 0 && (
            <>
              <SectionComponent
                sectionName="Opções"
                setIsOpen={setIsOpen}
                isOpen={isOpen}
              />
              <Collapse isOpen={isOpen}>
                <Row className="mobile-btns-top">
                  <div onClick={() => setModalWordCloud(true)}>
                    Nuvem de Palavras
                  </div>

                  <div onClick={() => exportReport()}>Exportar Mensagens</div>
                </Row>
              </Collapse>
            </>
          )}
        </div>

        <div className="header-btns-container">
          {chat.data.length > 0 && (
            <>
              <Button
                color="success"
                className="btn-rounded waves-effect waves-light events-button-add"
                onClick={() => setModalWordCloud(true)}
              >
                Nuvem de Palavras
              </Button>

              <Button
                color="success"
                className="btn-rounded waves-effect waves-light events-button-add"
                onClick={() => exportReport()}
              >
                Exportar Mensagens
              </Button>
            </>
          )}
        </div>
      </div>

      {/* chatFixed só é populado quando existe chat e o snapshot já chegou, então
      o acesso precisa ser opcional para não estourar */}
      <ChatFixed
        data={firestoreData?.data?.chatFixed?.[0] ?? null}
        setChatFixData={setChatFixData}
        setConfirmRemoveChatFix={setConfirmRemoveChatFix}
        loadingChatFixed={loadingChatFixed}
        lengthData={firestoreData?.data?.chatFixed?.length ?? 0}
      />

      <div
        style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
      >
        <button
          type="button"
          className="btn-reset-table border-0"
          onClick={() => resetPagination()}
        >
          <i className="mdi mdi-refresh" style={{ marginRight: 5 }} />
          Refresh
        </button>
      </div>

      <div className="table-responsive bg-white">
        <Table className="table mb-0">
          <thead className="table-light">
            <tr>
              <th>ID do Participante</th>
              <th>Nome</th>
              <th>Mensagem</th>
              <th>Data e hora</th>
              <th>Ação</th>
            </tr>
          </thead>

          <tbody>
            {chat.loading ? (
              <LoadingSkelentonTable lines={3} cols={5} />
            ) : (
              chat.data.map((e) => (
                <tr key={e.id}>
                  <td>{e.userId}</td>
                  <td>{e.name}</td>
                  <td>{e.msg}</td>
                  <td>{formatDate(e.date)}</td>
                  <td>
                    <i
                      role="button"
                      title="Deletar mensagem"
                      className="mdi mdi-trash-can text-danger font-size-17"
                      onClick={() => {
                        setDeleteData(e);
                        setConfirmDelete(true);
                      }}
                    />

                    <i
                      role="button"
                      title="Fixar mensagem"
                      className="mdi mdi-pin-outline text-primary font-size-17"
                      onClick={() => {
                        setChatFixData(e);
                        setConfirmChatFix(true);
                      }}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <Row style={{ marginTop: 20 }}>
        <Col md={12}>
          <Pagination
            data={chat.data}
            search={search}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            countData={countData}
            maxItemPerPage={itemsPerPage}
            setMaxItemPerPage={setItemsPerPage}
            maxPageButtonsToShow={3}
          />
        </Col>
      </Row>
    </div>
  );
}
